/**
 * AICoachWidget — Floating 380px chat widget with drill-in navigation.
 *
 * Two views:
 * - chatList: conversation cards with "New Chat" button
 * - singleChat: full chat with ← Back, messages, input
 */

import { useState, useRef, useEffect } from 'react';
import { Sparkles, X, ArrowLeft, Send, Plus, Trash2, MessageSquare, AlertCircle } from 'lucide-react';
import useAICoachStore from '../../stores/useAICoachStore';
import useAICreditStore from '../../stores/useAICreditStore';
import { usePageContext } from '../../contexts/PageContextProvider';
import ActionCard from './ActionCard';
import './AICoachWidget.css';

// ---------- Relative Time Helper ----------

function relativeTime(isoString) {
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------- Chat List View ----------

function ChatListView() {
  const conversations = useAICoachStore(s => s.conversations);
  const createConversation = useAICoachStore(s => s.createConversation);
  const openConversation = useAICoachStore(s => s.openConversation);
  const deleteConversation = useAICoachStore(s => s.deleteConversation);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = (e, convId) => {
    e.stopPropagation();
    if (deletingId === convId) {
      // Second click = confirm
      deleteConversation(convId);
      setDeletingId(null);
    } else {
      setDeletingId(convId);
      // Reset after 3 seconds
      setTimeout(() => setDeletingId(null), 3000);
    }
  };

  return (
    <div className="ai-widget__chat-list">
      <button className="btn btn-ghost btn-lg ai-widget__new-chat" onClick={createConversation}>
        <Plus size={16} />
        New Chat
      </button>

      {conversations.length === 0 ? (
        <div className="ai-widget__empty">
          <Sparkles size={32} className="ai-widget__empty-icon" />
          <p>Start your first conversation with your AI coach</p>
          <button className="btn btn-primary btn-sm" onClick={createConversation}>
            New Chat
          </button>
        </div>
      ) : (
        <div className="ai-widget__conversations">
          {conversations.map(conv => (
            <div
              key={conv.id}
              className="ai-widget__conv-card"
              onClick={() => openConversation(conv.id)}
            >
              <div className="ai-widget__conv-card-left">
                <MessageSquare size={16} className="ai-widget__conv-icon" />
                <div className="ai-widget__conv-info">
                  <span className="ai-widget__conv-title">{conv.title}</span>
                  <span className="ai-widget__conv-time">{relativeTime(conv.updatedAt)}</span>
                </div>
              </div>
              <button
                className={`btn btn-ghost btn-sm btn-icon ai-widget__conv-delete ${deletingId === conv.id ? 'ai-widget__conv-delete--confirm' : ''}`}
                onClick={(e) => handleDelete(e, conv.id)}
                title={deletingId === conv.id ? 'Click again to delete' : 'Delete conversation'}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Single Chat View ----------

function SingleChatView() {
  const activeConv = useAICoachStore(s => s.getActiveConversation());
  const showChatList = useAICoachStore(s => s.showChatList);
  const sendMessage = useAICoachStore(s => s.sendMessage);
  const isLoading = useAICoachStore(s => s.isLoading);
  const error = useAICoachStore(s => s.error);
  const clearError = useAICoachStore(s => s.clearError);
  const suggestedPrompts = useAICoachStore(s => s.suggestedPrompts);
  const { pageContext } = usePageContext();
  const getSmartPrompts = useAICoachStore(s => s.getSmartPrompts);

  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Get page-aware prompts
  const smartPrompts = getSmartPrompts(pageContext);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConv?.messages?.length, isLoading]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    sendMessage(input.trim(), { pageContext });
    setInput('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    // Auto-grow textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  const handlePromptClick = (prompt) => {
    sendMessage(prompt, { pageContext });
  };

  if (!activeConv) return null;

  const hasUserMessages = activeConv.messages.some(m => m.role === 'user');

  return (
    <div className="ai-widget__single-chat">
      {/* Navigation header */}
      <div className="ai-widget__chat-nav">
        <button className="btn btn-ghost btn-sm btn-icon ai-widget__back-btn" onClick={showChatList}>
          <ArrowLeft size={18} />
        </button>
        <span className="ai-widget__chat-title">{activeConv.title}</span>
      </div>

      {/* Messages */}
      <div className="ai-widget__messages">
        {activeConv.messages.map((msg, i) => (
          <div key={i} className={`ai-widget__msg ai-widget__msg--${msg.role}`}>
            {msg.role === 'assistant' && (
              <div className="ai-widget__msg-avatar">
                <Sparkles size={14} />
              </div>
            )}
            <div className="ai-widget__msg-bubble">
              {msg.content.split('\n').map((line, j) => (
                <p key={j}>{line || <br />}</p>
              ))}
              {/* Phase 3: Action cards */}
              {msg.actions && msg.actions.length > 0 && (
                <ActionCard
                  actions={msg.actions}
                  actionsApplied={msg.actionsApplied}
                  convId={activeConv.id}
                  msgIndex={i}
                />
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {isLoading && (
          <div className="ai-widget__msg ai-widget__msg--assistant">
            <div className="ai-widget__msg-avatar">
              <Sparkles size={14} />
            </div>
            <div className="ai-widget__msg-bubble">
              <div className="ai-widget__typing">
                <span /><span /><span />
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="ai-widget__error">
            <AlertCircle size={14} />
            <span>
              {error === 'NO_CREDITS_OR_KEY'
                ? 'AI credits depleted. Add BYOK in Settings to continue.'
                : error === 'INSUFFICIENT_CREDITS'
                  ? 'Not enough credits. Add BYOK in Settings.'
                  : error === 'NO_API_KEY'
                    ? 'Add your Gemini API key in Settings.'
                    : error.startsWith('QUOTA_EXCEEDED')
                      ? 'Quota exceeded. Try again later.'
                      : 'Something went wrong. Try again.'}
            </span>
            <button className="btn btn-ghost btn-sm ai-widget__error-dismiss" onClick={clearError}>×</button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested prompts (shown when no user messages yet) */}
      {!hasUserMessages && !isLoading && (
        <div className="ai-widget__prompts">
          {smartPrompts.map((prompt, i) => (
            <button
              key={i}
              className="ai-widget__prompt-chip"
              onClick={() => handlePromptClick(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="ai-widget__input-area">
        <textarea
          ref={textareaRef}
          className="ai-widget__input"
          value={input}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder="Ask your AI coach..."
          rows={1}
          disabled={isLoading}
        />
        <button
          className="btn btn-tertiary btn-icon ai-widget__send-btn"
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

// ---------- Main Widget ----------

export default function AICoachWidget() {
  const isWidgetOpen = useAICoachStore(s => s.isWidgetOpen);
  const widgetView = useAICoachStore(s => s.widgetView);
  const closeWidget = useAICoachStore(s => s.closeWidget);
  const credits = useAICreditStore(s => s.credits);
  const aiMode = useAICreditStore(s => s.getAIMode('ai_coach'));

  // Close on Escape
  useEffect(() => {
    if (!isWidgetOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeWidget();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isWidgetOpen, closeWidget]);

  if (!isWidgetOpen) return null;

  return (
    <div className="ai-widget animate-slide-up">
      {/* Header */}
      <div className="ai-widget__header">
        <div className="ai-widget__header-left">
          <Sparkles size={18} className="ai-widget__header-icon" />
          <span className="ai-widget__header-title">AI Coach</span>
          {aiMode === 'proxy' ? (
            <span className="ai-widget__badge" style={{ backgroundColor: 'var(--info-soft)', color: 'var(--link-hover)' }}>
              {credits.toLocaleString()} Credits
            </span>
          ) : aiMode === 'byok' ? (
            <span className="ai-widget__badge" style={{ backgroundColor: 'var(--positive-soft)', color: 'var(--positive)' }}>
              BYOK
            </span>
          ) : (
            <span className="ai-widget__badge" style={{ backgroundColor: 'var(--error-soft)', color: 'var(--error)' }}>
              No Credits
            </span>
          )}
        </div>
        <button className="btn btn-ghost btn-sm btn-icon ai-widget__close" onClick={closeWidget}>
          <X size={16} />
        </button>
      </div>

      {/* Content */}
      {widgetView === 'chatList' ? <ChatListView /> : <SingleChatView />}
    </div>
  );
}
